import React from "react";
import { connect } from "react-redux";

import language from "../../../language";
import { Switch } from "../../Input";
import { Separator } from "../../Separator";
import { hasNoSample } from "./helpers";

/**
 * Stands in for the crossmatch panel on an emergency release that waived it.
 *
 * Waiving the testing is the point of the form, so nothing here blocks: the
 * doctor asked for uncrossmatched or untested units and signed for it. What it
 * does is refuse to let the unit go out silently. If the group on the bag is
 * not the group on the patient, that is said plainly and has to be ticked
 * through, so an emergency release cannot quietly become a wrong-unit error
 * that nobody noticed until the post-release results came back.
 */
const groupOf = (request) =>
  request?.emergencyRelease?.patientBloodGroup ||
  request?.sampleBloodGroup ||
  request?.patient?.bloodGroup ||
  "";

// "O Positive", "O POS" and "O+" are the same group written three ways, and
// all three are in the data. Compared raw they would read as a mismatch, and a
// warning that fires on nothing is a warning people learn to click past.
const normalise = (group) =>
  String(group || "")
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/POSITIVE$|POS$/, "+")
    .replace(/NEGATIVE$|NEG$/, "-");

const EmergencyIssueCheck = ({
  request,
  scannedUnit,
  confirmed,
  onConfirm,
  defaultLanguage,
}) => {
  const patientGroup = groupOf(request);
  const unitGroup = scannedUnit?.bloodGroup || "";

  // Only a comparison we can actually make counts as a mismatch. An
  // unidentified patient has no group on file, and inventing a warning out of
  // that would train people to click past the real ones.
  const mismatch =
    !!patientGroup &&
    !!unitGroup &&
    normalise(patientGroup) !== normalise(unitGroup);

  return (
    <div>
      <Separator
        title={language[defaultLanguage].emergency_pre_transfusion_not_completed}
        titleClassName="text-primary"
      />

      <p className="text-muted">
        {hasNoSample(request)
          ? language[defaultLanguage].emergency_no_sample_note
          : language[defaultLanguage].emergency_release}
      </p>

      {mismatch && (
        <div className="mb-2">
          <p className="text-error text-bold mb-1">
            {language[defaultLanguage].emergency_product_mismatch_title}
          </p>
          <p className="text-error">
            {language[defaultLanguage].patient_blood_group}: {patientGroup}
            {" - "}
            {language[defaultLanguage].product}: {unitGroup}
          </p>
        </div>
      )}

      <div
        onClick={!confirmed ? onConfirm : undefined}
        style={{ cursor: !confirmed ? "pointer" : "default" }}
      >
        <Switch
          label={
            mismatch
              ? language[defaultLanguage].emergency_product_mismatch_confirm
              : language[defaultLanguage].compatible_with_patient
          }
          checked={!!confirmed}
        />
      </div>
      <br />
    </div>
  );
};

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;

  return { defaultLanguage };
};

export default connect(mapStateToProps)(EmergencyIssueCheck);
